import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import "./dashboard.css";
import Instance from "../../AxiosConfig";
import { Spin, Segmented } from "antd";

const CertificationGraph = () => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [monthWiseData, setMonthWiseData] = useState([]);
  const [manualMax, setManualMax] = useState("");
  const [viewMode, setViewMode] = useState("closure");
  const [selectedManager, setSelectedManager] = useState("");
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);


  // 🔹 Month name → month number map
  const monthMap = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12,
  };

  // 🔹 Set default month , year & Manager 
  useEffect(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const monthNames = Object.keys(monthMap);

    setSelectedYear(currentYear.toString());
    setSelectedMonth(monthNames[now.getMonth()]);
  }, []);

  useEffect(() => {
    if (!selectedMonth || !selectedYear) return;

    const fetchChartData = async () => {
      try {
        setLoading(true);

        const res = await Instance.get(
          "/dashboard/v2/agent-monthwise-closure",
          {
            params: {
              month: monthMap[selectedMonth],
              year: Number(selectedYear),
              ...(selectedManager && {
                managerId: selectedManager,
              }),
            },
          }
        );

        console.log("Chart Data Response:", res.data);

        setMonthWiseData(res.data?.performance || []);
      } catch (err) {
        console.error("Error fetching chart:", err);
        setMonthWiseData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [selectedMonth, selectedYear, selectedManager]);

  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const res = await Instance.get("/agents/managers", {
          params: {
            role: "MANAGER",
            noPagination: true,
          },
        });

        setManagers(res.data?.managers || []);
      } catch (err) {
        console.error(err);
      }
    };

    fetchManagers();
  }, []);



  useLayoutEffect(() => {
    if (!chartRef.current) return;
    const ctx = chartRef.current.getContext("2d");

    let labels = monthWiseData.map((item) => item.agentName);
    const mappedData = monthWiseData.map((item) => {
      if (item.closure === 0 && item.target === 0) {
        return 0;
      }

      return Number(item[viewMode]) || 0;
    });

    const DEFAULT_MAX =
      viewMode === "achievement"
        ? 100
        : 1000000;
    const dataMax = Math.max(...mappedData, 0);

    const finalMax = manualMax
      ? Number(manualMax)
      : viewMode === "achievement"
        ? Math.max(100, Math.ceil(dataMax * 1.1))
        : Math.max(
          DEFAULT_MAX,
          Math.ceil(dataMax * 1.1)
        );
    const stepSize = Math.ceil(finalMax / 5);

    const data = {
      labels,
      datasets: [
        {
          label:
            viewMode === "closure"
              ? "Closure Amount"
              : "Achievement %",
          data: mappedData,
          borderColor: monthWiseData.map((item) =>
            item.achievement < 100 ? "#EF4444" : item.barColor || "#136AD5"
          ),

          borderWidth: monthWiseData.map((item) =>
            item.achievement < 100 ? 2 : 0
          ),
          backgroundColor: monthWiseData.map(
            (item) => item.barColor || "#136AD5"
          ),
          fill: true,
          maxBarThickness: 30,
          barPercentage: 1.0,
          categoryPercentage: 0.6,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          max: finalMax,
          ticks: {
            stepSize: stepSize,
            callback: (value) =>
              viewMode === "achievement"
                ? `${value}%`
                : Number(value).toLocaleString("en-IN"),
            color: "#9ca3af",
            font: { size: 14 },
          },
          grid: { color: "rgba(0,0,0,0.1)", drawBorder: false },
        },
        x: {
          ticks: { color: "#64748B", font: { size: 8 } },
          grid: { display: false },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const item = monthWiseData[ctx.dataIndex];

              // No target assigned
              if (item.closure === 0 && item.target === 0) {
                return "No Target Assigned";
              }

              if (viewMode === "closure") {
                return `Closure : ₹${item.closure.toLocaleString("en-IN")}`;
              }

              return [
                `Achievement : ${item.achievement}%`,
                `Closure : ₹${item.closure.toLocaleString("en-IN")}`,
                `Target : ₹${item.target.toLocaleString("en-IN")}`,
                `Manager : ${item.managerName || "-"}`,
              ];
            },
          },
        },
        backgroundColor: "#136AD5",
        titleColor: "#fff",
        bodyColor: "#fff",
        padding: 10,
        displayColors: false,
      },

    };

    if (chartInstanceRef.current) chartInstanceRef.current.destroy();
    chartInstanceRef.current = new Chart(ctx, { type: "bar", data, options });

    return () => chartInstanceRef.current?.destroy();
  }, [monthWiseData, manualMax, viewMode]);

  return (
    <div className="chart-card mt-5">
      <div className="chart-header">
        <h2 className="chart-title">Agent Individual Performances</h2>

        <div className="chart-dropdowns">
          <select
            className="chart-dropdown"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <option value="" disabled>
              Select Year
            </option>
            {Array.from({ length: 2050 - 2025 + 1 }, (_, i) => 2025 + i).map(
              (year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              )
            )}
          </select>

          <select
            className="chart-dropdown"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="" disabled>
              Select Month
            </option>
            {Object.keys(monthMap).map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>

          <select
            className="chart-dropdown"
            value={selectedManager}
            onChange={(e) => setSelectedManager(e.target.value)}
          >
            <option value="">All Managers</option>

            {managers.map((manager) => (
              <option
                key={manager._id}
                value={manager._id}
              >
                {manager.agentName}
              </option>
            ))}
          </select>
          <Segmented
            value={viewMode}
            onChange={setViewMode}
            options={[
              {
                label: "Closure",
                value: "closure",
              },
              {
                label: "Achievement %",
                value: "achievement",
              },
            ]}
          />

          <input
            type="number"
            placeholder="Enter Max Amount"
            value={manualMax}
            onChange={(e) => setManualMax(e.target.value)}
            className="chart-dropdown"
            style={{ width: "180px" }}
          />
        </div>
      </div>

      <div className="chart-body" style={{ position: "relative" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
            }}
          >
            <Spin size="large" />
          </div>
        )}

        <canvas ref={chartRef}></canvas>
      </div>
    </div>
  );
};

export default CertificationGraph;
